import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configurar axios para enviar Bearer token em toda requisição autenticada
const setupAxiosInterceptor = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
};

const AuthContext = createContext();

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Restaurar sessão do localStorage e validar via /auth/me
  useEffect(() => {
    const storedToken = localStorage.getItem('santa-ana-token');
    const storedUser = localStorage.getItem('santa-ana-user');
    if (storedToken) {
      setupAxiosInterceptor(storedToken);
      setToken(storedToken);
      if (storedUser) {
        try { setUser(JSON.parse(storedUser)); } catch (e) { /* ignore */ }
      }
      // Revalidar com backend (token pode estar expirado)
      axios.get(`${API}/auth/me`)
        .then(resp => {
          setUser(resp.data);
          localStorage.setItem('santa-ana-user', JSON.stringify(resp.data));
        })
        .catch(() => {
          // Token inválido/expirado
          localStorage.removeItem('santa-ana-token');
          localStorage.removeItem('santa-ana-user');
          setupAxiosInterceptor(null);
          setUser(null);
          setToken(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password, opts = {}) => {
    try {
      // opts.portal: 'vendedor' ou 'cliente' → usa /auth/portal-login (BLOQUEIA admin).
      // Sem portal → /auth/login (URL secreta do admin, aceita qualquer role).
      // opts.totp_code: código TOTP de 6 dígitos quando o backend pedir.
      const portal = opts.portal;
      const url = portal ? `${API}/auth/portal-login?portal=${portal}` : `${API}/auth/login`;
      const body = { email, password };
      if (opts.totp_code) body.totp_code = opts.totp_code;
      const response = await axios.post(url, body);
      if (response.data?.success) {
        const newToken = response.data.token;
        const newUser = response.data.user;
        setToken(newToken);
        setUser(newUser);
        localStorage.setItem('santa-ana-token', newToken);
        localStorage.setItem('santa-ana-user', JSON.stringify(newUser));
        setupAxiosInterceptor(newToken);
        return { success: true, user: newUser, mustChangePassword: !!response.data.must_change_password };
      }
      return { success: false, message: response.data?.message };
    } catch (error) {
      const detail = error.response?.data?.detail;
      let message = 'Erro ao fazer login';
      if (typeof detail === 'string') message = detail;
      else if (Array.isArray(detail)) message = detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
      // sinaliza pro caller que precisa de TOTP
      const totpRequired = message === 'TOTP_REQUIRED';
      return { success: false, message, totpRequired };
    }
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (e) { /* ignore */ }
    setUser(null);
    setToken(null);
    localStorage.removeItem('santa-ana-token');
    localStorage.removeItem('santa-ana-user');
    setupAxiosInterceptor(null);
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      // refresh user data
      const meResp = await axios.get(`${API}/auth/me`);
      setUser(meResp.data);
      localStorage.setItem('santa-ana-user', JSON.stringify(meResp.data));
      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      return { success: false, message: typeof detail === 'string' ? detail : 'Erro ao trocar senha' };
    }
  };

  const updateUser = (updatedData) => {
    const newUser = { ...user, ...updatedData };
    setUser(newUser);
    localStorage.setItem('santa-ana-user', JSON.stringify(newUser));
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, updateUser, changePassword }}>
      {children}
    </AuthContext.Provider>
  );
};
